import React from 'react';
import PageHero from '../components/PageHero';
import Footer from '../components/Footer';
import { Link } from 'react-router-dom';

const HowToDesign = () => {
  return (
    <main className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 min-h-screen">
      <PageHero title="How to Design Your Perfect Night Lamp" />

      <div className="max-w-7xl mx-auto px-4 md:px-6 py-12">
        {/* Breadcrumb */}
        <nav className="text-sm mb-8" aria-label="Breadcrumb">
          <ol className="flex flex-wrap gap-1 text-gray-600 dark:text-gray-400">
            <li>
              <Link to="/" className="hover:text-[#ff5d4d] transition-colors">Home</Link>
            </li>
            <li className="px-2">›</li>
            <li className="text-gray-900 dark:text-white font-medium">How to Design</li>
          </ol>
        </nav>

        {/* Header Section */}
        <header className="mb-12 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
            Create Your Custom Night Lamp
          </h1>
          <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
            Transform your cherished memories into a beautiful illuminated night lamp in just four simple steps.
            Switch templates anytime, preview in stunning 3D, and bring your design to life.
          </p>

          {/* Quick Navigation */}
          <nav className="mt-8 flex flex-wrap justify-center gap-3">
            {[
              { href: '#templates', label: '1. Choose Template', icon: '🎨' },
              { href: '#design', label: '2. Design', icon: '✏️' },
              { href: '#preview', label: '3. 3D Preview', icon: '👁️' },
              { href: '#order', label: '4. Order', icon: '🛒' },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                className="px-6 py-3 rounded-xl bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-semibold shadow-md hover:shadow-lg hover:scale-105 transition-all duration-200 border border-gray-200 dark:border-gray-700"
              >
                <span className="mr-2">{item.icon}</span>
                {item.label}
              </a>
            ))}
          </nav>
        </header>

        {/* Section 1: Choose Template */}
        <section id="templates" className="mb-20">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff5d4d] to-[#ff7d6d] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                1
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Choose Your Template</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left: Description */}
              <div className="space-y-6">
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  Start by selecting a layout that perfectly fits your photos. Each template is designed to showcase your memories beautifully.
                </p>

                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6">
                  <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Available Templates:</h3>
                  <ul className="space-y-3 text-gray-700 dark:text-gray-300">
                    <li className="flex items-start gap-3">
                      <span className="text-[#ff5d4d] font-bold">•</span>
                      <span><strong>Single Photo</strong> - One large image with side text</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#ff5d4d] font-bold">•</span>
                      <span><strong>Double Photo</strong> - Two images with side text</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#ff5d4d] font-bold">•</span>
                      <span><strong>Triple Photo</strong> - Three images with side text</span>
                    </li>
                    <li className="flex items-start gap-3">
                      <span className="text-[#ff5d4d] font-bold">•</span>
                      <span><strong>Quad Photo</strong> - Four images with bottom text</span>
                    </li>
                  </ul>
                </div>

                <div className="bg-green-50 dark:bg-green-900/20 rounded-xl p-4 border-l-4 border-green-500">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Pro Tip:</strong> You can switch templates at any time during design. Text is optional and can be toggled on or off!
                  </p>
                </div>

                <Link
                  to="/builder"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#ff5d4d] to-[#ff7d6d] hover:from-[#ff4d3d] hover:to-[#ff6d5d] text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all duration-200"
                >
                  Start Designing Now →
                </Link>
              </div>

              {/* Right: Images */}
              <div className="space-y-4">
                <img
                  src="/images/IMG-step1-grid@2x.jpg"
                  alt="Template selection showing four different layout options"
                  className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
                />
                <div className="grid grid-cols-2 gap-4">
                  <img
                    src="/images/IMG-step1-card-1@2x.jpg"
                    alt="Single layout template with side text"
                    className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
                  />
                  <img
                    src="/images/IMG-step1-card-2@2x.jpg"
                    alt="Multi layout template design"
                    className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 2: Design Your Lamp */}
        <section id="design" className="mb-20">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff5d4d] to-[#ff7d6d] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                2
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Design Your Lamp</h2>
            </div>

            <p className="text-lg text-gray-700 dark:text-gray-300 mb-8">
              Upload your favorite photos, personalize with messages or stickers, and fine-tune every detail to perfection.
            </p>

            <div className="grid md:grid-cols-5 gap-8">
              {/* Left: Main Editor Image */}
              <div className="md:col-span-3">
                <img
                  src="/images/IMG-step2-editor-wide@2x.jpg"
                  alt="Design editor with upload and customization"
                  className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
                />
              </div>

              {/* Right: Feature Cards */}
              <div className="md:col-span-2 space-y-4">
                {/* Upload Photos */}
                <details className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800 hover:shadow-lg transition-shadow duration-200">
                  <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer text-lg flex items-center gap-2">
                    📸 Upload Photos
                  </summary>
                  <div className="mt-4 space-y-3">
                    <p className="text-gray-700 dark:text-gray-300">Add your cherished memories to the design:</p>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Upload 1-4 images based on your template</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Drag to reposition, scroll to zoom</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Ensure photos cover slots completely</span>
                      </li>
                    </ul>
                    <div className="bg-white dark:bg-gray-700 rounded-lg p-3 text-xs text-gray-600 dark:text-gray-400">
                      <strong>Requirements:</strong> JPG/PNG, ≤12MB each, min. 1600×2000px
                    </div>
                    <img
                      src="/images/IMG-step2-upload-panel@2x.jpg"
                      alt="Upload panel controls"
                      className="w-full rounded-lg shadow-md mt-3"
                    />
                  </div>
                </details>

                {/* Text Customization */}
                <details className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800 hover:shadow-lg transition-shadow duration-200">
                  <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer text-lg flex items-center gap-2">
                    ✍️ Add Text
                  </summary>
                  <div className="mt-4 space-y-3">
                    <p className="text-gray-700 dark:text-gray-300">Personalize with a heartfelt message:</p>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Choose from multiple fonts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Adjust size, spacing & position</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Toggle italic styling</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Maximum 140 characters</span>
                      </li>
                    </ul>
                    <img
                      src="/images/IMG-step2-text-panel@2x.jpg"
                      alt="Text customization controls"
                      className="w-full rounded-lg shadow-md mt-3"
                    />
                  </div>
                </details>

                {/* Stickers */}
                <details className="bg-gradient-to-br from-pink-50 to-pink-100 dark:from-pink-900/20 dark:to-pink-800/20 rounded-xl p-6 border border-pink-200 dark:border-pink-800 hover:shadow-lg transition-shadow duration-200">
                  <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer text-lg flex items-center gap-2">
                    ⭐ Add Stickers
                  </summary>
                  <div className="mt-4 space-y-3">
                    <p className="text-gray-700 dark:text-gray-300">Enhance with decorative elements:</p>
                    <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Choose from dozens of stickers</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Drag to move, handles to resize/rotate</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#ff5d4d]">✓</span>
                        <span>Stickers stay within text area</span>
                      </li>
                    </ul>
                    <img
                      src="/images/IMG-step2-stickers-panel@2x.jpg"
                      alt="Sticker selection grid"
                      className="w-full rounded-lg shadow-md mt-3"
                    />
                  </div>
                </details>

                {/* Actions */}
                <details className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 border border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow duration-200">
                  <summary className="font-semibold text-gray-900 dark:text-white cursor-pointer text-lg flex items-center gap-2">
                    🎯 Available Actions
                  </summary>
                  <div className="mt-4 space-y-3 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-start gap-2">
                      <span className="text-[#ff5d4d] font-bold">→</span>
                      <span><strong>Save to Cloud:</strong> Store your design online</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#ff5d4d] font-bold">→</span>
                      <span><strong>Export PNG:</strong> Download flat image</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#ff5d4d] font-bold">→</span>
                      <span><strong>3D Preview:</strong> View in realistic 3D</span>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-[#ff5d4d] font-bold">→</span>
                      <span><strong>Order Now:</strong> Proceed to checkout</span>
                    </div>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        {/* Section 3: 3D Preview */}
        <section id="preview" className="mb-20">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff5d4d] to-[#ff7d6d] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                3
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Preview in Stunning 3D</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-center">
              {/* Left: Description */}
              <div className="space-y-6">
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  See exactly how your night lamp will look before ordering. Our realistic 3D preview lets you inspect every detail.
                </p>

                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-600 dark:text-blue-400 text-xl">🔄</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Rotate & Inspect</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Drag to rotate, scroll to zoom, and pan to explore every angle</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-purple-600 dark:text-purple-400 text-xl">🎨</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Black & White Preview</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">The preview shows B&W lithophane effect - it glows beautifully when lit!</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                      <span className="text-green-600 dark:text-green-400 text-xl">✏️</span>
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900 dark:text-white">Easy Adjustments</h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400">Not perfect? Click 'Back' to return to the editor and make changes</p>
                    </div>
                  </div>
                </div>

                <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-xl p-4 border-l-4 border-yellow-500">
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    <strong>Note:</strong> The final product is printed in grayscale and creates a magical glow effect when illuminated from behind!
                  </p>
                </div>
              </div>

              {/* Right: Image */}
              <div>
                <img
                  src="/images/IMG-step3-preview@2x.jpg"
                  alt="3D preview with realistic rendering"
                  className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Order & Confirm */}
        <section id="order" className="mb-20">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 md:p-12">
            <div className="flex items-center gap-4 mb-8">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#ff5d4d] to-[#ff7d6d] flex items-center justify-center text-white text-xl font-bold shadow-lg">
                4
              </div>
              <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Order & Secure Confirmation</h2>
            </div>

            <div className="grid md:grid-cols-2 gap-8 items-start">
              {/* Left: Process */}
              <div className="space-y-6">
                <p className="text-lg text-gray-700 dark:text-gray-300">
                  Our secure ordering process ensures your design is perfect before production begins.
                </p>

                <div className="space-y-4">
                  {[
                    {
                      step: '1',
                      title: 'Enter Your Details',
                      description: 'Click "Order Now" and fill in your shipping information and contact details',
                      color: 'blue'
                    },
                    {
                      step: '2',
                      title: 'Verify Your Email',
                      description: 'Check your inbox for a one-time verification code (OTP)',
                      color: 'purple'
                    },
                    {
                      step: '3',
                      title: 'Confirm Your Order',
                      description: 'Enter the OTP to securely confirm your order',
                      color: 'green'
                    },
                    {
                      step: '4',
                      title: 'Quality Check & Updates',
                      description: "We'll contact you via WhatsApp/email to reconfirm design and delivery details. If any adjustments are needed, we'll send a proof before production",
                      color: 'orange'
                    }
                  ].map((item) => (
                    <div key={item.step} className="flex items-start gap-4">
                      <div className={`w-10 h-10 rounded-full bg-${item.color}-100 dark:bg-${item.color}-900/30 flex items-center justify-center flex-shrink-0 font-bold text-${item.color}-600 dark:text-${item.color}-400`}>
                        {item.step}
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{item.title}</h4>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{item.description}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">📧 Email Verification</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">Double-check your email address - we'll send the OTP there!</p>
                  </div>
                  <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4">
                    <h4 className="font-semibold text-gray-900 dark:text-white mb-2">✅ Quality Assurance</h4>
                    <p className="text-xs text-gray-600 dark:text-gray-400">We check every design for quality before production starts</p>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">⏱️ Production & Delivery</h4>
                  <p className="text-sm text-gray-700 dark:text-gray-300">
                    Typical production and shipping time: 7-10 business days. We'll keep you updated every step of the way!
                  </p>
                </div>
              </div>

              {/* Right: Images */}
              <div className="space-y-6">
                <img
                  src="/images/IMG-step4-modal@2x.jpg"
                  alt="Order form with shipping and payment"
                  className="w-full rounded-xl shadow-lg hover:shadow-2xl transition-shadow duration-300"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Bottom CTA Section */}
        <div className="text-center bg-gradient-to-br from-[#ff5d4d] to-[#ff7d6d] rounded-2xl shadow-2xl p-12">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to Create Your Masterpiece?
          </h2>
          <p className="text-white/90 text-lg mb-8 max-w-2xl mx-auto">
            Start designing your custom night lamp now and bring your cherished memories to life with a beautiful illuminated display.
          </p>
          <Link
            to="/builder"
            className="inline-flex items-center gap-3 px-10 py-5 bg-white text-[#ff5d4d] rounded-xl font-bold text-lg shadow-xl hover:shadow-2xl hover:scale-105 transition-all duration-200"
          >
            Start Designing Now
            <span className="text-2xl">→</span>
          </Link>
        </div>
      </div>

      <Footer />
    </main>
  );
};

export default HowToDesign;
